import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axiosInstance from "../../api/axiosInstance";

import {
  Search,
  Plus,
  Edit3,
  Trash2,
  ShieldCheck,
  Users,
  KeyRound,
  X,
  Save,
  CheckSquare,
  Square,
  Eye,
  EyeOff,
} from "lucide-react";

const ROLE_API = "/api/roles";

/* =========================================================
   MENU GROUPS
   ========================================================= */

const MENU_GROUPS = [
  {
    key: "masters",
    title: "Masters",
    items: [
      "customers",
      "vehicles",
      "vendors",
      "employees",
      "vehicle_group",
      "vehicle_type",
      "hotels",
    ],
  },

  {
    key: "operations",
    title: "Operations",
    items: [
      "booking",
      "tripsheets",
      "purchase",
      "invoice",
      "proforma",
      "bunks",
      "fuel_indent",
    ],
  },

  {
    key: "accounts",
    title: "Accounts & Reports",
    items: [
      "accounts_chart",
      "vouchers",
      "accounts_reports",
      "reports",
    ],
  },

  {
    key: "settings",
    title: "Settings",
    items: [
      "settings_organisation",
      "settings_tax_compliance",
      "settings_users_roles",
      "settings_operation_controls",
      "settings_notifications",
      "settings_integrations",
    ],
  },
];

/* =========================================================
   FALLBACK MENU MASTER

   Backend also returns the master list. This fallback makes
   the screen resilient if the master endpoint is unavailable.
   ========================================================= */

const FALLBACK_MENU_MASTER = [
  {
    menu_key: "customers",
    label: "Customers",
  },
  {
    menu_key: "vehicles",
    label: "Vehicles",
  },
  {
    menu_key: "vendors",
    label: "Vendors",
  },
  {
    menu_key: "employees",
    label: "Employees",
  },
  {
    menu_key: "vehicle_group",
    label: "Vehicle Group",
  },
  {
    menu_key: "vehicle_type",
    label: "Vehicle Type",
  },
  {
    menu_key: "hotels",
    label: "Hotels",
  },
  {
    menu_key: "booking",
    label: "Booking",
  },
  {
    menu_key: "tripsheets",
    label: "Tripsheets",
  },
  {
    menu_key: "purchase",
    label: "Purchase",
  },
  {
    menu_key: "invoice",
    label: "Invoice",
  },
  {
    menu_key: "proforma",
    label: "Proforma",
  },
  {
    menu_key: "bunks",
    label: "Bunks",
  },
  {
    menu_key: "fuel_indent",
    label: "Fuel Indent",
  },
  {
    menu_key: "accounts_chart",
    label: "Accounts Chart",
  },
  {
    menu_key: "vouchers",
    label: "Vouchers",
  },
  {
    menu_key: "accounts_reports",
    label: "Accounts Reports",
  },
  {
    menu_key: "reports",
    label: "Reports",
  },

  /* SETTINGS - CARD LEVEL PERMISSIONS */

  {
    menu_key: "settings_organisation",
    label: "Organisation",
  },
  {
    menu_key: "settings_tax_compliance",
    label: "Tax & Compliance",
  },
  {
    menu_key: "settings_users_roles",
    label: "Users & Roles",
  },
  {
    menu_key: "settings_operation_controls",
    label: "Operation Controls",
  },
  {
    menu_key: "settings_notifications",
    label: "Reminders & Notifications",
  },
  {
    menu_key: "settings_integrations",
    label: "Integrations & Marketplace",
  },
];

/* =========================================================
   FORMAT ROLE NAME
   ========================================================= */

const formatRoleName = (name) => {
  return String(name || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
};

/* =========================================================
   ERROR MESSAGE
   ========================================================= */

const getErrorMessage = (
  error,
  fallback
) => {
  return (
    error?.response?.data?.message ||
    fallback
  );
};

/* =========================================================
   EMPTY MENU PERMISSION
   ========================================================= */

const createEmptyMenuPermission = (
  menuKey,
  label = ""
) => ({
  menu_key: menuKey,
  label,
  can_view: false,
  can_create: false,
  can_edit: false,
  can_delete: false,
});

/* =========================================================
   NORMALIZE MENU PERMISSIONS
   ========================================================= */

const normalizeMenuPermissions = (
  menuList
) => {
  const incoming = Array.isArray(menuList)
    ? menuList
    : [];

  const incomingMap = new Map(
    incoming.map((item) => [
      item.menu_key,
      item,
    ])
  );

  return FALLBACK_MENU_MASTER.map(
    (master) => {
      const saved =
        incomingMap.get(
          master.menu_key
        );

      return {
        menu_key: master.menu_key,
        label:
          saved?.label ||
          master.label,
        can_view:
          saved?.can_view === true,
        can_create:
          saved?.can_create === true,
        can_edit:
          saved?.can_edit === true,
        can_delete:
          saved?.can_delete === true,
      };
    }
  );
};

/* =========================================================
   COMPONENT
   ========================================================= */

export default function Roles() {
  /* =======================================================
     STATE
     ======================================================= */

  const [roles, setRoles] = useState([]);

  const [menuMaster, setMenuMaster] =
    useState(FALLBACK_MENU_MASTER);

  const [
    selectedMenuPermissions,
    setSelectedMenuPermissions,
  ] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [
    permissionLoading,
    setPermissionLoading,
  ] = useState(false);

  const [saving, setSaving] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [
    roleModalOpen,
    setRoleModalOpen,
  ] = useState(false);

  const [
    permissionModalOpen,
    setPermissionModalOpen,
  ] = useState(false);

  const [editingRole, setEditingRole] =
    useState(null);

  const [selectedRole, setSelectedRole] =
    useState(null);

  const [menuSearch, setMenuSearch] =
    useState("");

  const [form, setForm] =
    useState({
      name: "",
      description: "",
      is_default: false,
    });

  const [message, setMessage] =
    useState({
      type: "",
      text: "",
    });

  /* =======================================================
     MESSAGE
     ======================================================= */

  const showMessage = (
    text,
    type = "success"
  ) => {
    setMessage({
      text,
      type,
    });

    window.setTimeout(() => {
      setMessage({
        text: "",
        type: "",
      });
    }, 3500);
  };

  /* =======================================================
     LOAD ROLES
     ======================================================= */

  const loadRoles = async () => {
    try {
      setLoading(true);

      const response =
        await axiosInstance.get(
          ROLE_API
        );

      setRoles(
        response.data?.roles || []
      );
    } catch (error) {
      console.error(
        "Load roles error:",
        error
      );

      showMessage(
        getErrorMessage(
          error,
          "Failed to load roles."
        ),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     LOAD MENU MASTER
     ======================================================= */

  const loadMenuMaster = async () => {
    try {
      const response =
        await axiosInstance.get(
          `${ROLE_API}/menu-permissions/master`
        );

      const data =
        response.data?.menu_permissions;

      if (Array.isArray(data) && data.length) {
        setMenuMaster(
          data.map((item) => ({
            menu_key: item.menu_key,
            label:
              item.label ||
              formatRoleName(
                item.menu_key
              ),
          }))
        );
      }
    } catch (error) {
      console.warn(
        "Menu master load failed. Using fallback.",
        error
      );
    }
  };

  /* =======================================================
     INITIAL LOAD
     ======================================================= */

  useEffect(() => {
    loadRoles();
    loadMenuMaster();
  }, []);

  /* =======================================================
     FILTERED ROLES
     ======================================================= */

  const filteredRoles = useMemo(() => {
    const value =
      search.trim().toLowerCase();

    if (!value) {
      return roles;
    }

    return roles.filter((role) =>
      [
        role.name,
        role.description,
      ]
        .filter(Boolean)
        .some((item) =>
          String(item)
            .toLowerCase()
            .includes(value)
        )
    );
  }, [roles, search]);

  /* =======================================================
     OPEN ADD ROLE
     ======================================================= */

  const openAddRole = () => {
    setEditingRole(null);

    setForm({
      name: "",
      description: "",
      is_default: false,
    });

    setRoleModalOpen(true);
  };

  /* =======================================================
     OPEN EDIT ROLE
     ======================================================= */

  const openEditRole = (
    role
  ) => {
    setEditingRole(role);

    setForm({
      name: role.name || "",
      description:
        role.description || "",
      is_default:
        Boolean(role.is_default),
    });

    setRoleModalOpen(true);
  };

  /* =======================================================
     CLOSE ROLE MODAL
     ======================================================= */

  const closeRoleModal = () => {
    if (saving) {
      return;
    }

    setRoleModalOpen(false);
    setEditingRole(null);
  };

  /* =======================================================
     SAVE ROLE
     ======================================================= */

  const saveRole = async (
    event
  ) => {
    event.preventDefault();

    const roleName =
      form.name.trim();

    if (!roleName) {
      showMessage(
        "Role name is required.",
        "error"
      );
      return;
    }

    /* Admin is reserved */

    if (
      editingRole &&
      String(editingRole.name)
        .toLowerCase() ===
        "admin" &&
      roleName.toLowerCase() !==
        "admin"
    ) {
      showMessage(
        "The admin role cannot be renamed.",
        "error"
      );
      return;
    }

    try {
      setSaving(true);

      if (editingRole) {
        await axiosInstance.put(
          `${ROLE_API}/${editingRole.role_id}`,
          {
            name: roleName,
            description:
              form.description.trim(),
            is_default:
              String(
                editingRole.name
              ).toLowerCase() ===
              "admin"
                ? true
                : Boolean(
                    form.is_default
                  ),
          }
        );

        showMessage(
          "Role updated successfully."
        );
      } else {
        await axiosInstance.post(
          ROLE_API,
          {
            name: roleName,
            description:
              form.description.trim(),
            is_default:
              Boolean(
                form.is_default
              ),
          }
        );

        showMessage(
          "Role created successfully."
        );
      }

      setRoleModalOpen(false);
      setEditingRole(null);

      await loadRoles();
    } catch (error) {
      console.error(
        "Save role error:",
        error
      );

      showMessage(
        getErrorMessage(
          error,
          "Failed to save role."
        ),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     DELETE ROLE
     ======================================================= */

  const deleteRole = async (
    role
  ) => {
    const roleName =
      formatRoleName(role.name);

    if (
      String(role.name)
        .trim()
        .toLowerCase() ===
      "admin"
    ) {
      showMessage(
        "The admin role cannot be deleted.",
        "error"
      );
      return;
    }

    if (role.is_default) {
      showMessage(
        "The default role cannot be deleted.",
        "error"
      );
      return;
    }

    const confirmed =
      window.confirm(
        `Are you sure you want to delete the "${roleName}" role?\n\nThis role must not be assigned to any users.`
      );

    if (!confirmed) {
      return;
    }

    try {
      setSaving(true);

      await axiosInstance.delete(
        `${ROLE_API}/${role.role_id}`
      );

      showMessage(
        "Role deleted successfully."
      );

      await loadRoles();
    } catch (error) {
      console.error(
        "Delete role error:",
        error
      );

      showMessage(
        getErrorMessage(
          error,
          "Failed to delete role."
        ),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     OPEN PERMISSION MATRIX
     ======================================================= */

  const openPermissions = async (
    role
  ) => {
    try {
      setSelectedRole(role);

      setPermissionModalOpen(true);

      setPermissionLoading(true);

      setMenuSearch("");

      const response =
        await axiosInstance.get(
          `${ROLE_API}/${role.role_id}/permissions`
        );

      const serverMenus =
        response.data?.menu_permissions ||
        [];

      const normalized =
        normalizeMenuPermissions(
          serverMenus
        );

      setSelectedMenuPermissions(
        normalized
      );
    } catch (error) {
      console.error(
        "Load role menu permissions error:",
        error
      );

      showMessage(
        getErrorMessage(
          error,
          "Failed to load role permissions."
        ),
        "error"
      );

      setPermissionModalOpen(false);
      setSelectedRole(null);
    } finally {
      setPermissionLoading(false);
    }
  };

  /* =======================================================
     CLOSE PERMISSION MODAL
     ======================================================= */

  const closePermissionModal = () => {
    if (saving) {
      return;
    }

    setPermissionModalOpen(false);
    setSelectedRole(null);
    setSelectedMenuPermissions([]);
    setMenuSearch("");
  };

  /* =======================================================
     GET MENU PERMISSION
     ======================================================= */

  const getMenuPermission = (
    menuKey
  ) => {
    return (
      selectedMenuPermissions.find(
        (item) =>
          item.menu_key === menuKey
      ) ||
      createEmptyMenuPermission(
        menuKey
      )
    );
  };

  /* =======================================================
     TOGGLE SINGLE CHECKBOX
     ======================================================= */

  const toggleMenuPermission = (
    menuKey,
    permission
  ) => {
    setSelectedMenuPermissions(
      (previous) =>
        previous.map((item) => {
          if (
            item.menu_key !== menuKey
          ) {
            return item;
          }

          return {
            ...item,
            [permission]:
              !item[permission],
          };
        })
    );
  };

  /* =======================================================
     TOGGLE WHOLE ROW
     ======================================================= */

  const toggleRow = (
    menuKey
  ) => {
    setSelectedMenuPermissions(
      (previous) =>
        previous.map((item) => {
          if (
            item.menu_key !== menuKey
          ) {
            return item;
          }

          const allSelected =
            item.can_view &&
            item.can_create &&
            item.can_edit &&
            item.can_delete;

          return {
            ...item,
            can_view: !allSelected,
            can_create: !allSelected,
            can_edit: !allSelected,
            can_delete: !allSelected,
          };
        })
    );
  };

  /* =======================================================
     SELECT ALL VIEW
     ======================================================= */

  const selectAllView = () => {
    setSelectedMenuPermissions(
      (previous) =>
        previous.map((item) => ({
          ...item,
          can_view: true,
        }))
    );
  };

  /* =======================================================
     CLEAR ALL VIEW
     ======================================================= */

  const clearAllView = () => {
    setSelectedMenuPermissions(
      (previous) =>
        previous.map((item) => ({
          ...item,
          can_view: false,
        }))
    );
  };

  /* =======================================================
     SELECT ALL PERMISSIONS
     ======================================================= */

  const selectAllPermissions = () => {
    setSelectedMenuPermissions(
      (previous) =>
        previous.map((item) => ({
          ...item,
          can_view: true,
          can_create: true,
          can_edit: true,
          can_delete: true,
        }))
    );
  };

  /* =======================================================
     CLEAR ALL PERMISSIONS
     ======================================================= */

  const clearAllPermissions = () => {
    setSelectedMenuPermissions(
      (previous) =>
        previous.map((item) => ({
          ...item,
          can_view: false,
          can_create: false,
          can_edit: false,
          can_delete: false,
        }))
    );
  };

  /* =======================================================
     SAVE MENU PERMISSIONS
     ======================================================= */

  const savePermissions = async () => {
    if (!selectedRole) {
      return;
    }

    try {
      setSaving(true);

      const payload =
        selectedMenuPermissions.map(
          (item) => ({
            menu_key:
              item.menu_key,
            can_view:
              item.can_view === true,
            can_create:
              item.can_create === true,
            can_edit:
              item.can_edit === true,
            can_delete:
              item.can_delete === true,
          })
        );

      await axiosInstance.put(
        `${ROLE_API}/${selectedRole.role_id}/permissions`,
        {
          menu_permissions:
            payload,
        }
      );

      showMessage(
        "Permissions saved successfully."
      );

      setPermissionModalOpen(false);
      setSelectedRole(null);
      setSelectedMenuPermissions([]);

      await loadRoles();
    } catch (error) {
      console.error(
        "Save menu permissions error:",
        error
      );

      showMessage(
        getErrorMessage(
          error,
          "Failed to save permissions."
        ),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     FILTER MENU
     ======================================================= */

  const filteredMenuKeys =
    useMemo(() => {
      const value =
        menuSearch
          .trim()
          .toLowerCase();

      if (!value) {
        return new Set(
          menuMaster.map(
            (item) =>
              item.menu_key
          )
        );
      }

      return new Set(
        menuMaster
          .filter((item) =>
            [
              item.label,
              item.menu_key,
            ]
              .filter(Boolean)
              .some((valueItem) =>
                String(valueItem)
                  .toLowerCase()
                  .includes(value)
              )
          )
          .map(
            (item) =>
              item.menu_key
          )
      );
    }, [
      menuMaster,
      menuSearch,
    ]);

  /* =======================================================
     COUNTS
     ======================================================= */

  const selectedViewCount =
    selectedMenuPermissions.filter(
      (item) =>
        item.can_view
    ).length;

  const selectedActionCount =
    selectedMenuPermissions.reduce(
      (total, item) =>
        total +
        Number(item.can_create) +
        Number(item.can_edit) +
        Number(item.can_delete),
      0
    );

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="w-full h-full bg-white">

      {/* =================================================
          HEADER
          ================================================= */}

      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">

        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Roles
          </h2>

          <p className="text-xs text-gray-500 mt-0.5">
            Manage user roles and menu permissions
          </p>
        </div>

        <button
          type="button"
          onClick={openAddRole}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
        >
          <Plus size={16} />
          Add Role
        </button>
      </div>

      {/* =================================================
          SEARCH / SUMMARY
          ================================================= */}

      <div className="px-4 py-3 border-b border-gray-100">

        <div className="flex flex-col sm:flex-row gap-2 justify-between">

          <div className="relative w-full sm:w-80">

            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              placeholder="Search roles..."
              className="w-full h-9 pl-9 pr-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500">

            <span>
              Roles:
              <b className="ml-1 text-gray-800">
                {roles.length}
              </b>
            </span>

            <span>
              Menus:
              <b className="ml-1 text-gray-800">
                {menuMaster.length}
              </b>
            </span>

          </div>

        </div>
      </div>

      {/* =================================================
          MESSAGE
          ================================================= */}

      {message.text && (
        <div
          className={`mx-4 mt-3 px-3 py-2 rounded-md text-sm ${
            message.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* =================================================
          ROLE TABLE
          ================================================= */}

      <div className="p-4">

        <div className="border border-gray-200 rounded-lg overflow-hidden">

          <div className="overflow-x-auto">

            <table className="w-full text-sm">

              <thead className="bg-gray-50 border-b border-gray-200">

                <tr>

                  <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                    Role Name
                  </th>

                  <th className="text-left px-3 py-2.5 font-semibold text-gray-600">
                    Description
                  </th>

                  <th className="text-center px-3 py-2.5 font-semibold text-gray-600">
                    Users
                  </th>

                  <th className="text-center px-3 py-2.5 font-semibold text-gray-600">
                    Settings Access
                  </th>

                  <th className="text-center px-3 py-2.5 font-semibold text-gray-600">
                    Default
                  </th>

                  <th className="text-center px-3 py-2.5 font-semibold text-gray-600">
                    Actions
                  </th>

                </tr>

              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center py-10 text-gray-500"
                    >
                      Loading roles...
                    </td>
                  </tr>
                ) : filteredRoles.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center py-10 text-gray-500"
                    >
                      No roles found.
                    </td>
                  </tr>
                ) : (
                  filteredRoles.map(
                    (role) => (
                      <tr
                        key={
                          role.role_id
                        }
                        className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                      >

                        {/* ROLE */}

                        <td className="px-3 py-2.5">

                          <div className="flex items-center gap-2">

                            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center">

                              <ShieldCheck
                                size={16}
                                className="text-blue-600"
                              />

                            </div>

                            <div>

                              <div className="font-medium text-gray-800">
                                {formatRoleName(
                                  role.name
                                )}
                              </div>

                              {String(
                                role.name
                              ).toLowerCase() ===
                                "admin" && (
                                <span className="text-[10px] text-blue-600">
                                  System role
                                </span>
                              )}

                            </div>

                          </div>

                        </td>

                        {/* DESCRIPTION */}

                        <td className="px-3 py-2.5 text-gray-500 max-w-[300px]">

                          <span className="line-clamp-2">
                            {role.description ||
                              "—"}
                          </span>

                        </td>

                        {/* USERS */}

                        <td className="px-3 py-2.5 text-center">

                          <span className="inline-flex items-center gap-1 text-gray-700">

                            <Users
                              size={14}
                              className="text-gray-400"
                            />

                            {Number(
                              role.user_count ||
                                0
                            )}

                          </span>

                        </td>

                        {/* MENU ACCESS */}

                        <td className="px-3 py-2.5 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              openPermissions(
                                role
                              )
                            }
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                          >

                            <KeyRound
                              size={14}
                            />

                            {Number(
                              role.menu_count ??
                                0
                            )}

                          </button>

                        </td>

                        {/* DEFAULT */}

                        <td className="px-3 py-2.5 text-center">

                          {role.is_default ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                              Yes
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">
                              No
                            </span>
                          )}

                        </td>

                        {/* ACTIONS */}

                        <td className="px-3 py-2.5">

                          <div className="flex justify-center items-center gap-1">

                            <button
                              type="button"
                              onClick={() =>
                                openPermissions(
                                  role
                                )
                              }
                              title="Manage Permissions"
                              className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                            >
                              <ShieldCheck
                                size={16}
                              />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                openEditRole(
                                  role
                                )
                              }
                              title="Edit Role"
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                            >
                              <Edit3
                                size={16}
                              />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                deleteRole(
                                  role
                                )
                              }
                              title="Delete Role"
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            >
                              <Trash2
                                size={16}
                              />
                            </button>

                          </div>

                        </td>

                      </tr>
                    )
                  )
                )}

              </tbody>

            </table>

          </div>

        </div>

      </div>

      {/* =================================================
          ADD / EDIT ROLE MODAL
          ================================================= */}

      {roleModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">

          <div className="w-full max-w-md bg-white rounded-lg shadow-xl">

            {/* HEADER */}

            <div className="flex items-center justify-between px-4 py-3 border-b">

              <div>

                <h3 className="font-semibold text-gray-800">
                  {editingRole
                    ? "Edit Role"
                    : "Add Role"}
                </h3>

                <p className="text-xs text-gray-500 mt-0.5">
                  Define role details
                </p>

              </div>

              <button
                type="button"
                onClick={
                  closeRoleModal
                }
                disabled={saving}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={18} />
              </button>

            </div>

            {/* FORM */}

            <form
              onSubmit={saveRole}
              className="p-4 space-y-3"
            >

              <div>

                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Role Name
                </label>

                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        name:
                          e.target.value,
                      })
                    )
                  }
                  placeholder="e.g. Manager"
                  maxLength={100}
                  autoFocus
                  disabled={
                    editingRole &&
                    String(
                      editingRole.name
                    ).toLowerCase() ===
                      "admin"
                  }
                  className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />

              </div>

              <div>

                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Description
                </label>

                <textarea
                  value={
                    form.description
                  }
                  onChange={(e) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        description:
                          e.target.value,
                      })
                    )
                  }
                  rows={3}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">

                <input
                  type="checkbox"
                  checked={
                    form.is_default
                  }
                  disabled={
                    editingRole &&
                    String(
                      editingRole.name
                    ).toLowerCase() ===
                      "admin"
                  }
                  onChange={(e) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        is_default:
                          e.target.checked,
                      })
                    )
                  }
                  className="w-4 h-4"
                />

                Set as default role

              </label>

              {editingRole &&
                String(
                  editingRole.name
                ).toLowerCase() ===
                  "admin" && (
                  <div className="px-3 py-2 rounded-md bg-blue-50 border border-blue-100 text-xs text-blue-700">
                    Admin is the system default
                    role and cannot be renamed,
                    deleted, or unset as default.
                  </div>
                )}

              {/* FOOTER */}

              <div className="flex justify-end gap-2 pt-2">

                <button
                  type="button"
                  onClick={
                    closeRoleModal
                  }
                  disabled={saving}
                  className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium"
                >
                  <Save size={15} />

                  {saving
                    ? "Saving..."
                    : editingRole
                    ? "Update Role"
                    : "Save Role"}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

      {/* =================================================
          MENU PERMISSION MODAL
          ================================================= */}

      {permissionModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-3">

          <div className="w-full max-w-6xl h-[92vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">

            {/* =================================================
                MODAL HEADER
                ================================================= */}

            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">

              <div>

                <div className="flex items-center gap-2">

                  <ShieldCheck
                    size={19}
                    className="text-blue-600"
                  />

                  <h3 className="font-semibold text-gray-800">
                    Menu Permissions
                  </h3>

                </div>

                {selectedRole && (
                  <p className="text-xs text-gray-500 mt-0.5">

                    Role:

                    <span className="font-semibold text-gray-700 ml-1">
                      {formatRoleName(
                        selectedRole.name
                      )}
                    </span>

                    <span className="ml-3 text-blue-600">
                      {selectedViewCount}
                      {" "}
                      menus visible
                    </span>

                  </p>
                )}

              </div>

              <button
                type="button"
                onClick={
                  closePermissionModal
                }
                disabled={saving}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={19} />
              </button>

            </div>

            {/* =================================================
                TOOLBAR
                ================================================= */}

            <div className="px-4 py-2.5 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-2 flex-shrink-0">

              <div className="flex items-center gap-2 flex-wrap">

                <button
                  type="button"
                  onClick={
                    selectAllPermissions
                  }
                  disabled={
                    permissionLoading ||
                    saving
                  }
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-xs text-gray-700"
                >
                  <CheckSquare
                    size={14}
                  />
                  Select All
                </button>

                <button
                  type="button"
                  onClick={
                    clearAllPermissions
                  }
                  disabled={
                    permissionLoading ||
                    saving
                  }
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-xs text-gray-700"
                >
                  <Square
                    size={14}
                  />
                  Clear All
                </button>

                <button
                  type="button"
                  onClick={
                    selectAllView
                  }
                  disabled={
                    permissionLoading ||
                    saving
                  }
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-xs text-gray-700"
                >
                  <Eye size={14} />
                  View All
                </button>

                <button
                  type="button"
                  onClick={
                    clearAllView
                  }
                  disabled={
                    permissionLoading ||
                    saving
                  }
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-xs text-gray-700"
                >
                  <EyeOff size={14} />
                  Hide All
                </button>

              </div>

              <div className="relative w-52">

                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="text"
                  value={menuSearch}
                  onChange={(e) =>
                    setMenuSearch(
                      e.target.value
                    )
                  }
                  placeholder="Search menu..."
                  className="w-full h-8 pl-8 pr-2 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

              </div>

            </div>

            {/* =================================================
                COLUMN HEADER
                ================================================= */}

            <div className="px-4 py-2 border-b bg-white flex-shrink-0">

              <div className="grid grid-cols-[minmax(220px,1fr)_80px_80px_80px_80px] gap-0 items-center text-xs font-semibold text-gray-600">

                <div>
                  Menu
                </div>

                <div className="text-center">
                  View
                </div>

                <div className="text-center">
                  Create
                </div>

                <div className="text-center">
                  Edit
                </div>

                <div className="text-center">
                  Delete
                </div>

              </div>

            </div>

            {/* =================================================
                PERMISSION BODY
                ================================================= */}

            <div className="flex-1 overflow-y-auto px-4 py-2">

              {permissionLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                  Loading permissions...
                </div>
              ) : (
                <div className="space-y-3">

                  {MENU_GROUPS.map(
                    (group) => {

                      const groupItems =
                        group.items
                          .map(
                            (menuKey) =>
                              menuMaster.find(
                                (item) =>
                                  item.menu_key ===
                                  menuKey
                              )
                          )
                          .filter(
                            Boolean
                          )
                          .filter(
                            (item) =>
                              filteredMenuKeys.has(
                                item.menu_key
                              )
                          );

                      if (
                        groupItems.length ===
                        0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={
                            group.key
                          }
                          className="border border-gray-200 rounded-md overflow-hidden"
                        >

                          {/* GROUP HEADER */}

                          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">

                            <span className="text-xs font-semibold text-gray-700">
                              {group.title}
                            </span>

                            <span className="ml-2 text-[10px] text-gray-400">
                              {
                                groupItems.length
                              }{" "}
                              menus
                            </span>

                          </div>

                          {/* MENU ROWS */}

                          <div>

                            {groupItems.map(
                              (
                                menu
                              ) => {

                                const permission =
                                  getMenuPermission(
                                    menu.menu_key
                                  );

                                const allChecked =
                                  permission.can_view &&
                                  permission.can_create &&
                                  permission.can_edit &&
                                  permission.can_delete;

                                return (
                                  <div
                                    key={
                                      menu.menu_key
                                    }
                                    className={`grid grid-cols-[minmax(220px,1fr)_80px_80px_80px_80px] gap-0 items-center border-b last:border-b-0 ${
                                      permission.can_view
                                        ? "bg-blue-50/30"
                                        : "bg-white"
                                    } hover:bg-gray-50`}
                                  >

                                    {/* MENU */}

                                    <div className="px-3 py-2.5">

                                      <div className="flex items-center gap-2">

                                        <button
                                          type="button"
                                          onClick={() =>
                                            toggleRow(
                                              menu.menu_key
                                            )
                                          }
                                          disabled={
                                            saving
                                          }
                                          title="Toggle all permissions"
                                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                                            allChecked
                                              ? "bg-blue-600 border-blue-600 text-white"
                                              : permission.can_view
                                              ? "border-blue-400 bg-blue-50"
                                              : "border-gray-300 bg-white"
                                          }`}
                                        >
                                          {allChecked && (
                                            <span className="text-[9px] font-bold">
                                              ✓
                                            </span>
                                          )}
                                        </button>

                                        <span className="text-xs font-medium text-gray-700">
                                          {
                                            menu.label
                                          }
                                        </span>

                                      </div>

                                    </div>

                                    {/* VIEW */}

                                    <div className="flex justify-center">

                                      <input
                                        type="checkbox"
                                        checked={
                                          permission.can_view
                                        }
                                        onChange={() =>
                                          toggleMenuPermission(
                                            menu.menu_key,
                                            "can_view"
                                          )
                                        }
                                        disabled={
                                          saving
                                        }
                                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                                      />

                                    </div>

                                    {/* CREATE */}

                                    <div className="flex justify-center">

                                      <input
                                        type="checkbox"
                                        checked={
                                          permission.can_create
                                        }
                                        onChange={() =>
                                          toggleMenuPermission(
                                            menu.menu_key,
                                            "can_create"
                                          )
                                        }
                                        disabled={
                                          saving
                                        }
                                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                                      />

                                    </div>

                                    {/* EDIT */}

                                    <div className="flex justify-center">

                                      <input
                                        type="checkbox"
                                        checked={
                                          permission.can_edit
                                        }
                                        onChange={() =>
                                          toggleMenuPermission(
                                            menu.menu_key,
                                            "can_edit"
                                          )
                                        }
                                        disabled={
                                          saving
                                        }
                                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                                      />

                                    </div>

                                    {/* DELETE */}

                                    <div className="flex justify-center">

                                      <input
                                        type="checkbox"
                                        checked={
                                          permission.can_delete
                                        }
                                        onChange={() =>
                                          toggleMenuPermission(
                                            menu.menu_key,
                                            "can_delete"
                                          )
                                        }
                                        disabled={
                                          saving
                                        }
                                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                                      />

                                    </div>

                                  </div>
                                );
                              }
                            )}

                          </div>

                        </div>
                      );
                    }
                  )}

                </div>
              )}

            </div>

            {/* =================================================
                FOOTER
                ================================================= */}

            <div className="px-4 py-3 border-t bg-white flex items-center justify-between flex-shrink-0">

              <div className="flex items-center gap-4 text-xs text-gray-500">

                <span>
                  Visible Menus:
                  <b className="ml-1 text-gray-800">
                    {
                      selectedViewCount
                    }
                  </b>
                </span>

                <span>
                  Action Permissions:
                  <b className="ml-1 text-gray-800">
                    {
                      selectedActionCount
                    }
                  </b>
                </span>

              </div>

              <div className="flex items-center gap-2">

                <button
                  type="button"
                  onClick={
                    closePermissionModal
                  }
                  disabled={saving}
                  className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={
                    savePermissions
                  }
                  disabled={
                    saving ||
                    permissionLoading
                  }
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium"
                >

                  <Save size={15} />

                  {saving
                    ? "Saving..."
                    : "Save Permissions"}

                </button>

              </div>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}